import { FieldArray } from 'formik';
import { DateTime } from 'luxon';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { date, number, object, string } from 'yup';

import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Cabecalho from 'Component/Cabecalho';
import Form, {
  Botao as BotaoForm,
  CaixaDeSelecao,
  CaixaDeTexto,
  Campo,
  Contexto,
  Selecao,
} from 'Component/Form';
import Icone from 'Component/Icone';
import LayoutDefault from 'Component/Layout';
import HorarioDeTrabalho from 'Interface/HorariosDeTrabalho';
import VinculoEmpregaticio from 'Interface/VinculosEmpregaticios';
import {
  FORMAS_DE_CONTRATACAO,
  REGIMES_DE_TRABALHO,
  SIM_OU_NAO,
} from 'View/constants';
import { useRecuperarDepartamentosQuery } from 'View/Service/Default/Departamentos';
import { useRecuperarEmpresasQuery } from 'View/Service/Default/Empresas';
import { useRecuperarFuncoesQuery } from 'View/Service/Default/Funcoes';
import { useRemoverHorarioDeTrabalhoMutation } from 'View/Service/Default/HorariosDeTrabalho';
import {
  useEditarVinculoEmpregaticioMutation,
  useRecuperarVinculoEmpregaticioQuery,
} from 'View/Service/Default/VinculosEmpregaticios';

const regras = object({
  empresa_id: number().required('Obrigatório'),
  departamento_id: number().required('Obrigatório'),
  funcao_id: number().required('Obrigatório'),
  codigo_contabil: string().nullable().optional(),
  codigo_folha: string().nullable().optional(),
  centro_de_custo: string().nullable().optional(),
  cbo: string().nullable().optional(),
  admissao: date().required('Obrigatório'),
  rescisao: date().nullable().optional(),
  regime: number().required('Obrigatório'),
  forma_de_contratacao: number().required('Obrigatório'),
  carga_horaria: number().required('Obrigatório'),
  salario: number().required('Obrigatório'),
  vale_alimentacao: number().required('Obrigatório'),
  convenio_odontologico: number().required('Obrigatório'),
  recebe_vale_transporte: number().nullable().optional(),
  recebe_ajuda_de_custo: number().nullable().optional(),
  tem_convenio_medico: number().nullable().optional(),
  codigo_no_ponto: string().nullable().optional(),
  periodo_de_experiencia1: number().required('Obrigatório'),
  periodo_de_experiencia2: number().required('Obrigatório'),
});

const Editar = (): React.ReactElement => {
  const navegacao = useNavigate();
  const toast = useToast();
  const { id = 0 } = useParams();
  const [editarVinculo] = useEditarVinculoEmpregaticioMutation();
  const [removerHorario] = useRemoverHorarioDeTrabalhoMutation();

  const { data: { vinculo } = {}, isFetching: buscandoVinculo } =
    useRecuperarVinculoEmpregaticioQuery({
      id: id,
      contain: ['HorariosDeTrabalho'],
    });
  const { data: { empresas } = {} } = useRecuperarEmpresasQuery({
    finder: 'listaJson',
    sort: 'nome',
    limit: 1000,
  });
  const { data: { departamentos } = {} } = useRecuperarDepartamentosQuery({
    finder: 'listaJson',
    sort: 'nome',
    limit: 1000,
  });
  const { data: { funcoes } = {} } = useRecuperarFuncoesQuery({
    finder: 'listaJson',
    sort: 'nome',
    limit: 1000,
  });

  const enviar = async (dados: VinculoEmpregaticio): Promise<void> => {
    try {
      await editarVinculo({
        ...dados,
        options: { contain: ['HorariosDeTrabalho'] },
      }).unwrap();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });

      navegacao(`/usuarios/editar/${vinculo?.usuario_id}`);
    } catch (error) {
      toast({
        titulo: 'Erro ao adicionar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const remover = async (horario: HorarioDeTrabalho): Promise<void> => {
    try {
      await removerHorario(horario).unwrap();

      toast({ conteudo: 'Removido com sucesso!', tema: 'success' });
    } catch (error) {
      toast({
        titulo: 'Erro ao remover',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const periodo1 = vinculo?.admissao
    .data()
    .plus({ days: vinculo.periodo_de_experiencia1 });
  const periodo2 = vinculo?.admissao
    .data()
    .plus({ days: vinculo.periodo_de_experiencia2 });

  const diffPeriodo1 = Math.ceil(
    periodo1?.diff(DateTime.now(), 'days').days || 0
  );
  const diffPeriodo2 = Math.ceil(
    periodo2?.diff(DateTime.now(), 'days').days || 0
  );

  return (
    <LayoutDefault>
      <Cabecalho
        titulo="Editando vinculo empregatício"
        descricao="Preencha o formulário e clique em enviar para editar o vinculo"
      />
      <Form
        onSubmit={enviar}
        regras={regras}
        inicial={vinculo}
        desabilitado={buscandoVinculo}
      >
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="bg-white shadow-sm rounded p-4">
              <div className="row">
                <div className="col-md-6">
                  <Selecao
                    name="empresa_id"
                    rotulo="Empresa"
                    opcoes={empresas}
                  />
                </div>
                <div className="col-md-6">
                  <Selecao
                    name="departamento_id"
                    rotulo="Departamento"
                    opcoes={departamentos}
                  />
                </div>
                <div className="col-md-8">
                  <Selecao name="funcao_id" rotulo="Funções" opcoes={funcoes} />
                </div>
                <div className="col-md-4">
                  <Campo name="codigo_contabil" rotulo="Código contabil" />
                </div>
                <div className="col-md-4">
                  <Campo name="codigo_folha" rotulo="Código na folha" />
                </div>
                <div className="col-md-4">
                  <Campo name="centro_de_custo" rotulo="Centro de custo" />
                </div>
                <div className="col-md-4">
                  <Campo name="cbo" rotulo="CBO" />
                </div>
                <div className="col-md-6">
                  <Campo
                    type="date"
                    name="admissao"
                    rotulo="Data de admissão"
                    prefixar={<Icone nome="calendar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    type="date"
                    name="rescisao"
                    rotulo="Data de rescisão"
                    prefixar={<Icone nome="calendar" />}
                  />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="regime"
                    rotulo="Regime de trabalho"
                    opcoes={REGIMES_DE_TRABALHO}
                  />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="forma_de_contratacao"
                    rotulo="Forma de contratação"
                    opcoes={FORMAS_DE_CONTRATACAO}
                  />
                </div>
                <div className="col-md-4">
                  <Campo name="carga_horaria" rotulo="Carga horária" />
                </div>
                <div className="col-md-4">
                  <Campo
                    type="number"
                    name="salario"
                    rotulo="Salário"
                    prefixar={<Icone nome="cash-coin" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    type="number"
                    name="vale_alimentacao"
                    rotulo="Vale alimentação"
                    prefixar={<Icone nome="cart" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    type="number"
                    name="convenio_odontologico"
                    rotulo="Convênio odontológico"
                    prefixar={<Icone nome="clipboard" />}
                  />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="recebe_vale_transporte"
                    rotulo="Recebe vale transporte?"
                    opcoes={SIM_OU_NAO}
                  />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="recebe_ajuda_de_custo"
                    rotulo="Recebe ajuda de custo?"
                    opcoes={SIM_OU_NAO}
                  />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="tem_convenio_medico"
                    rotulo="Tem convênio médico?"
                    opcoes={SIM_OU_NAO}
                  />
                </div>
                <div className="col-md-6">
                  <Campo name="codigo_no_ponto" rotulo="Código no ponto" />
                </div>
                <div className="col-md-6">
                  <Selecao
                    name="bate_ponto"
                    rotulo="Bate ponto"
                    opcoes={SIM_OU_NAO}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="periodo_de_experiencia1"
                    rotulo="Período de experiência 1"
                    prefixar={<Icone nome="alarm" />}
                  />
                  {diffPeriodo1 > 0 && (
                    <small className="text-muted d-block mt-n3 mb-3">
                      Termina em {diffPeriodo1} dia(s) (
                      {DateTime.now()
                        .plus({ days: diffPeriodo1 })
                        .toLocaleString()}
                      )
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <Campo
                    name="periodo_de_experiencia2"
                    rotulo="Período de experiência 2"
                    prefixar={<Icone nome="alarm" />}
                  />
                  {diffPeriodo2 > 0 && (
                    <small className="text-muted d-block mt-n3 mb-3">
                      Termina em {diffPeriodo2} dia(s) (
                      {DateTime.now()
                        .plus({ days: diffPeriodo2 })
                        .toLocaleString()}
                      )
                    </small>
                  )}
                </div>
                <div className="col-md-12">
                  <CaixaDeTexto name="observacoes" rotulo="Observações" />
                </div>
                <div className="col-md-12">
                  <p className="fw-bold fs-3">Horários de trabalho</p>
                </div>
                <div className="col-md-12">
                  <Contexto>
                    {({ values }) => (
                      <FieldArray name="horarios_de_trabalho">
                        {({ remove, push }) => (
                          <React.Fragment>
                            {values?.horarios_de_trabalho?.map(
                              (horario: HorarioDeTrabalho, indice: number) => (
                                <div
                                  key={indice.toString()}
                                  className={`row ${
                                    indice && 'border-top pt-3'
                                  }`}
                                >
                                  <div className="col-md-3">
                                    <CaixaDeSelecao
                                      name={`horarios_de_trabalho.${indice}.segunda`}
                                      rotulo="Segunda-feira"
                                    />
                                    <CaixaDeSelecao
                                      name={`horarios_de_trabalho.${indice}.terca`}
                                      rotulo="Terça-feira"
                                    />
                                    <CaixaDeSelecao
                                      name={`horarios_de_trabalho.${indice}.quarta`}
                                      rotulo="Quarta-feira"
                                    />
                                  </div>
                                  <div className="col-md-3">
                                    <CaixaDeSelecao
                                      name={`horarios_de_trabalho.${indice}.quinta`}
                                      rotulo="Quinta-feira"
                                    />
                                    <CaixaDeSelecao
                                      name={`horarios_de_trabalho.${indice}.sexta`}
                                      rotulo="Sexta-feira"
                                    />
                                    <CaixaDeSelecao
                                      name={`horarios_de_trabalho.${indice}.sabado`}
                                      rotulo="Sábado"
                                    />
                                  </div>
                                  <div className="col-md-3">
                                    <CaixaDeSelecao
                                      name={`horarios_de_trabalho.${indice}.domingo`}
                                      rotulo="Domingo"
                                    />
                                  </div>
                                  <div className="col-md-3">
                                    <div className="text-end">
                                      {horario.id ? (
                                        <Botao
                                          tema="danger"
                                          icone="trash-fill"
                                          onClick={() => remover(horario)}
                                          confirmacao="Tem certeza que deseja remover esse horário? Essa ação é irreversível!"
                                          className="mb-3"
                                        >
                                          Remover
                                        </Botao>
                                      ) : (
                                        <Botao
                                          tema="danger"
                                          icone="trash-fill"
                                          onClick={() => remove(indice)}
                                          className="mb-3"
                                        >
                                          Remover
                                        </Botao>
                                      )}
                                    </div>
                                  </div>
                                  <div className="col-md-12">
                                    <div className="row">
                                      <div className="col-md-3">
                                        <Campo
                                          type="time"
                                          name={`horarios_de_trabalho.${indice}.entrada`}
                                          rotulo="Entrada"
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <Campo
                                          type="time"
                                          name={`horarios_de_trabalho.${indice}.inicio_do_intervalo`}
                                          rotulo="Início do intervalo"
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <Campo
                                          type="time"
                                          name={`horarios_de_trabalho.${indice}.termino_do_intervalo`}
                                          rotulo="Término do intervalo"
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <Campo
                                          type="time"
                                          name={`horarios_de_trabalho.${indice}.saida`}
                                          rotulo="Saída"
                                        />
                                      </div>
                                    </div>
                                    <div className="row">
                                      <div className="col-md-3">
                                        <Campo
                                          type="time"
                                          name={`horarios_de_trabalho.${indice}.entrada_fds`}
                                          rotulo="Entrada (FDS)"
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <Campo
                                          type="time"
                                          name={`horarios_de_trabalho.${indice}.inicio_do_intervalo_fds`}
                                          rotulo="Início do inter. (FDS)"
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <Campo
                                          type="time"
                                          name={`horarios_de_trabalho.${indice}.termino_do_intervalo_fds`}
                                          rotulo="Térm. do inter. (FDS)"
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <Campo
                                          type="time"
                                          name={`horarios_de_trabalho.${indice}.saida_fds`}
                                          rotulo="Saída (FDS)"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                            <Botao
                              tema="success"
                              icone="plus"
                              onClick={() => push({})}
                            >
                              Adicionar
                            </Botao>
                          </React.Fragment>
                        )}
                      </FieldArray>
                    )}
                  </Contexto>
                </div>
              </div>

              <div className="mt-5">
                <BotaoForm>Enviar</BotaoForm>
                <BotaoLink
                  tema="light"
                  to={`/usuarios/editar/${vinculo?.usuario_id}`}
                  className="ms-2"
                >
                  Cancelar
                </BotaoLink>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </LayoutDefault>
  );
};

export default Editar;
